import { Component } from '@angular/core';
import { LucideAngularModule, Check, Smartphone, Wifi, CreditCard, WifiOff, ArrowUpRight } from 'lucide-angular';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-africa',
  standalone: true,
  imports: [LucideAngularModule, TranslateModule],
  template: `
    <section id="africa" class="py-20 md:py-28 bg-dark-100 relative overflow-hidden">
      <div class="max-w-7xl mx-auto px-5 md:px-10">

        <div class="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          <!-- Left: Copy -->
          <div data-aos="fade-right">
            <p class="text-accent-green font-bold text-xs tracking-[0.2em] uppercase mb-3">{{ 'AFRICA.LABEL' | translate }}</p>
            <h2 class="text-3xl md:text-[2.75rem] font-black mb-5 leading-[1.1]">
              {{ 'AFRICA.TITLE' | translate }}
            </h2>
            <p class="text-base text-gray-450 leading-relaxed mb-10">
              {{ 'AFRICA.DESC' | translate }}
            </p>

            <div class="space-y-4">
              <!-- Mobile Money natif -->
              <div class="flex items-start gap-4 bg-dark-300 rounded-xl p-4 border border-white/[0.04] hover:border-accent-green/15 transition-colors">
                <div class="w-9 h-9 rounded-lg bg-accent-green/10 flex items-center justify-center flex-shrink-0">
                  <lucide-icon [img]="Smartphone" class="w-4 h-4 text-accent-green"></lucide-icon>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-white font-bold text-sm mb-0.5">{{ 'AFRICA.FEAT_1_TITLE' | translate }}</p>
                  <p class="text-gray-550 text-xs">{{ 'AFRICA.FEAT_1_DESC' | translate }}</p>
                </div>
                <div class="flex gap-1.5 mt-1 flex-shrink-0">
                  <img src="payment/omlogo.png" alt="OM" class="w-5 h-5 bg-white p-0.5 rounded object-contain" />
                  <img src="payment/momologo.png" alt="MoMo" class="w-5 h-5 bg-white p-0.5 rounded object-contain" />
                </div>
              </div>

              <!-- Diaspora payments -->
              <div class="flex items-start gap-4 bg-dark-300 rounded-xl p-4 border border-white/[0.04] hover:border-accent-green/15 transition-colors">
                <div class="w-9 h-9 rounded-lg bg-accent-green/10 flex items-center justify-center flex-shrink-0">
                  <lucide-icon [img]="CreditCard" class="w-4 h-4 text-accent-green"></lucide-icon>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-white font-bold text-sm mb-0.5">{{ 'AFRICA.FEAT_2_TITLE' | translate }}</p>
                  <p class="text-gray-550 text-xs">{{ 'AFRICA.FEAT_2_DESC' | translate }}</p>
                </div>
              </div>

              <!-- Low-data optimisé -->
              <div class="flex items-start gap-4 bg-dark-300 rounded-xl p-4 border border-white/[0.04] hover:border-accent-green/15 transition-colors">
                <div class="w-9 h-9 rounded-lg bg-accent-green/10 flex items-center justify-center flex-shrink-0">
                  <lucide-icon [img]="WifiOff" class="w-4 h-4 text-accent-green"></lucide-icon>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-white font-bold text-sm mb-0.5">{{ 'AFRICA.FEAT_3_TITLE' | translate }}</p>
                  <p class="text-gray-550 text-xs">{{ 'AFRICA.FEAT_3_DESC' | translate }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Phone mockup -->
          <div class="relative max-w-[280px] mx-auto w-full" data-aos="fade-left" data-aos-delay="200">
            <!-- Ambient glow -->
            <div class="absolute -inset-10 bg-accent-green/[0.03] rounded-full blur-[80px] pointer-events-none"></div>
            
            <!-- Phone frame -->
            <div class="relative bg-dark-300 rounded-[2rem] border border-white/[0.08] shadow-deep overflow-hidden">
              <!-- Status bar -->
              <div class="flex items-center justify-between px-6 pt-3 pb-1">
                <span class="text-[10px] text-gray-550 font-medium">09:41</span>
                <div class="w-16 h-4 bg-dark-100 rounded-full"></div>
                <div class="flex items-center gap-1">
                  <lucide-icon [img]="Wifi" class="w-2.5 h-2.5 text-gray-550"></lucide-icon>
                  <div class="w-4 h-2 border border-gray-550 rounded-sm relative">
                    <div class="absolute inset-0.5 bg-accent-green rounded-[1px]" style="width:70%"></div>
                  </div>
                </div>
              </div>
              
              <!-- Screen content -->
              <div class="px-5 pb-6 pt-3">
                <!-- App header -->
                <div class="flex items-center justify-between mb-5">
                  <div class="flex items-center gap-2">
                    <img src="logo.png" alt="COLORFOL" class="h-3" />
                  </div>
                  <div class="w-6 h-6 rounded-full bg-dark-100 border border-white/[0.06]"></div>
                </div>

                <div class="text-center mb-5">
                  <div class="w-14 h-14 rounded-full bg-accent-green/10 border-2 border-accent-green/30 flex items-center justify-center mx-auto mb-3">
                    <lucide-icon [img]="Check" class="w-7 h-7 text-accent-green"></lucide-icon>
                  </div>
                  <p class="text-base font-black text-white mb-0.5">{{ 'AFRICA.MOCKUP_TITLE' | translate }}</p>
                  <p class="text-gray-550 text-[10px]">{{ 'AFRICA.MOCKUP_SUBTITLE' | translate }}</p>
                </div>
                
                <!-- Amount -->
                <div class="bg-dark-100 rounded-xl p-4 border border-white/[0.04] mb-2.5">
                  <p class="text-gray-550 text-[10px] mb-1">{{ 'AFRICA.MOCKUP_AMOUNT_LABEL' | translate }}</p>
                  <div class="flex items-baseline gap-1">
                    <span class="text-2xl font-black text-white">2,500</span>
                    <span class="text-sm font-bold text-gray-450">{{ 'AFRICA.MOCKUP_CURRENCY' | translate }}</span>
                  </div>
                </div>

                <!-- Details -->
                <div class="bg-dark-100 rounded-xl border border-white/[0.04] divide-y divide-white/[0.04]">
                  <div class="flex justify-between items-center px-4 py-2.5">
                    <span class="text-gray-550 text-[10px]">{{ 'AFRICA.MOCKUP_METHOD' | translate }}</span>
                    <div class="flex items-center gap-1.5">
                      <img src="payment/omlogo.png" alt="OM" class="w-4 h-4 bg-white p-0.5 rounded-sm object-contain" />
                      <span class="text-gray-350 text-[10px] font-medium">Orange Money</span>
                    </div>
                  </div>
                  <div class="flex justify-between items-center px-4 py-2.5">
                    <span class="text-gray-550 text-[10px]">{{ 'AFRICA.MOCKUP_FAN' | translate }}</span>
                    <span class="text-gray-350 text-[10px] font-medium">Moussa D. · Dakar</span>
                  </div>
                  <div class="flex justify-between items-center px-4 py-2.5">
                    <span class="text-gray-550 text-[10px]">{{ 'AFRICA.MOCKUP_PROJECT' | translate }}</span>
                    <span class="text-white text-[10px] font-bold">{{ 'AFRICA.MOCKUP_PROJECT_VAL' | translate }}</span>
                  </div>
                </div>
                
                <!-- Action button -->
                <button class="w-full mt-3 py-2.5 bg-accent-green text-dark-100 text-xs font-bold rounded-xl flex items-center justify-center gap-1">
                  {{ 'AFRICA.MOCKUP_BTN' | translate }}
                  <lucide-icon [img]="ArrowUpRight" class="w-3 h-3"></lucide-icon>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  `
})
export class AfricaComponent {
  readonly Check = Check;
  readonly Smartphone = Smartphone;
  readonly Wifi = Wifi;
  readonly CreditCard = CreditCard;
  readonly WifiOff = WifiOff;
  readonly ArrowUpRight = ArrowUpRight;
}
